import { useRef, type ReactNode } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useT } from '../i18n';

interface ConfirmSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** What happens, in plain words, including whether it can be undone. */
  body: ReactNode;
  /** The thing being acted on (a file, a message), shown above the question. */
  preview?: ReactNode;
  /** A round icon above the title (dialog style, e.g. leaving a family). */
  icon?: ReactNode;
  confirmLabel: string;
  confirmIcon?: ReactNode;
  cancelLabel?: string;
  onConfirm: () => void;
  isLoading?: boolean;
  /** The confirm button waits for something (a typed name, D188). */
  confirmDisabled?: boolean;
  /** No second button: the confirm button is the only way on (progress). */
  hideCancel?: boolean;
  /** Extra actions between confirm and cancel (e.g. "Delete for me"). */
  extra?: ReactNode;
  danger?: boolean;
  /** A centred card on phones too, for bigger decisions (leaving a family). */
  centered?: boolean;
}

// Confirmations (design: "Confirmations & downloads"): a bottom sheet on
// phones, a centred card on larger screens. Big, plainly worded buttons; the
// safe choice is always there and named for what it keeps.
export function ConfirmSheet({
  isOpen,
  onClose,
  title,
  body,
  preview,
  icon,
  confirmLabel,
  confirmIcon,
  cancelLabel,
  onConfirm,
  isLoading,
  confirmDisabled = false,
  hideCancel = false,
  extra,
  danger = true,
  centered = false,
}: ConfirmSheetProps) {
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const sheet = phone && !centered;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const t = useT();

  const content = (
    <Stack
      spacing={4}
      textAlign={icon ? 'center' : 'left'}
      align={icon ? 'center' : 'stretch'}
    >
      {preview}
      {icon && (
        <Box
          w="64px"
          h="64px"
          borderRadius="full"
          bg={danger ? 'danger.tint' : 'accent.tint'}
          color={danger ? 'danger' : 'accent'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          aria-hidden="true"
        >
          {icon}
        </Box>
      )}
      <Stack spacing="6px">
        <Heading as="h2" fontSize="24px" letterSpacing="-0.01em">
          {title}
        </Heading>
        {/* A list or several paragraphs can't sit inside a <p>. */}
        {typeof body === 'string' ? (
          <Text fontSize="16px" lineHeight="24px" color="ink.body">
            {body}
          </Text>
        ) : (
          <Box fontSize="16px" lineHeight="24px" color="ink.body">
            {body}
          </Box>
        )}
      </Stack>
      <Stack spacing="10px" pt={1} alignSelf="stretch">
        <Button
          h="56px"
          fontSize="17px"
          bg={danger ? 'danger.solid' : 'accent.solid'}
          color="white"
          _hover={{ opacity: 0.9 }}
          _active={{ opacity: 0.85 }}
          leftIcon={confirmIcon as React.ReactElement | undefined}
          onClick={onConfirm}
          isLoading={isLoading}
          isDisabled={confirmDisabled}
        >
          {confirmLabel}
        </Button>
        {extra}
        <Button
          display={hideCancel ? 'none' : undefined}
          ref={cancelRef}
          h="56px"
          fontSize="17px"
          variant="outline"
          colorScheme="gray"
          borderColor="line"
          bg="surface"
          onClick={onClose}
        >
          {cancelLabel ?? t('common.cancel')}
        </Button>
      </Stack>
    </Stack>
  );

  if (sheet) {
    return (
      <Drawer
        isOpen={isOpen}
        placement="bottom"
        onClose={onClose}
        initialFocusRef={cancelRef}
      >
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          role="alertdialog"
          aria-label={title}
          pb="max(env(safe-area-inset-bottom), 20px)"
        >
          <DrawerBody px={5} pt="10px" pb={0}>
            <Box
              w="40px"
              h="5px"
              borderRadius="3px"
              bg="gray.200"
              mx="auto"
              mb={4}
              aria-hidden="true"
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      initialFocusRef={cancelRef}
      size="md"
    >
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent
        mx={5}
        borderRadius="28px"
        bg="surface"
        role="alertdialog"
        aria-label={title}
        boxShadow="0 24px 48px -12px rgba(31, 29, 27, 0.45)"
      >
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
